import './add-billing-admin-form.scss';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { AnalyticsTrackAction, AnalyticsTrackSubject, analytics } from '@atlassian/temp-analytics';
import {
  AddBillingAdminsFields,
  InfoPanel,
  NotificationTypes,
  errorNotificationBuilder,
  isAccess,
  successNotificationBuilder,
  useNotification,
  useUpdateTransactionAccount,
} from '@css-xp/base';
import { BillingAdmin, CommerceAddress, Maybe } from '@css-xp/graphtypes';
import { UpdateTransactionAccountPayload } from '@atlassian/cssxp-types-billing';
import { Permissions } from '@atlassian/cssxp-typescommon';
import { get, isNil, set } from 'lodash';
import React, { useCallback, useRef, useState } from 'react';

interface FormData {
  recipients?: string[];
}

type Props = {
  transactionAccountId?: string;
  invoiceGroupId?: string;
  billingAdmins?: (BillingAdmin | undefined | null)[];
  billToAddress: Maybe<CommerceAddress>;
  onClose: () => void;
  onComplete: () => void;
  transactionAccountNumber?: string;
  header?: string;
  subheader?: string;
  isReactivateFlow?: boolean;
  shouldForceAddBillingAdmin?: boolean;
  isSubtextHidden?: boolean;
  sideBarClassName?: string;
  validDomains?: (string | null | undefined)[];
  isPartnerAccount?: boolean;
};

const AddBillingAdminsForm = ({
  billingAdmins = [],
  invoiceGroupId,
  transactionAccountId,
  onClose,
  onComplete,
  transactionAccountNumber,
  header,
  subheader,
  isReactivateFlow = false,
  shouldForceAddBillingAdmin = false,
  isSubtextHidden = false,
  sideBarClassName = '',
  validDomains,
  isPartnerAccount,
}: Props) => {
  const { sendSuccessNotification, setNotificationSent, sendErrorNotification } = useNotification();
  const notificationOptions = successNotificationBuilder().isVersion(NotificationTypes.FLAG);

  // FIXME :: Use sendNotification flog on useUpdateTransactionAccount to send notifications
  const onUpdateTransactionComplete = useCallback(
    (updatedPayload?: UpdateTransactionAccountPayload) => {
      const billingAdminCount = updatedPayload?.modifiedBillingAdmins?.emailsOrAaidsToAdd?.length;
      const commonMessage = `You've added ${billingAdminCount ?? ''} ${
        isPartnerAccount ? 'billing admins' : 'billing admins to billing account'
      } ${transactionAccountNumber}`;
      let message;
      if (billingAdminCount && billingAdminCount <= 5) {
        message = (
          <div>
            {commonMessage}:
            <ul className="mt-4">
              {updatedPayload?.modifiedBillingAdmins?.emailsOrAaidsToAdd?.map((billingAdmin) => (
                <li key={billingAdmin}>{billingAdmin}</li>
              ))}
            </ul>
          </div>
        );
      } else {
        message = commonMessage;
      }
      const notification = notificationOptions
        .withId('success-billing-admins')
        .withHeader(isReactivateFlow ? 'Reactivated billing account' : 'Update successful')
        .withMessage(message)
        .build();

      sendSuccessNotification(notification);
      setNotificationSent(true);
      onComplete();
      analytics.sendTrackEvent({
        action: isReactivateFlow ? AnalyticsTrackAction.REACTIVATE : AnalyticsTrackAction.UPDATED,
        actionSubject: AnalyticsTrackSubject.BILLING_ACCOUNT,
        attributes: {
          transactionAccountNumber: transactionAccountNumber,
        },
      });
    },
    [notificationOptions, sendSuccessNotification, setNotificationSent, onComplete, transactionAccountNumber]
  );

  // FIXME :: Use sendNotification flog on useUpdateTransactionAccount to send notifications
  const onUpdateTransactionError = useCallback(
    (errorList?: (() => JSX.Element)[]) => {
      const notificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader(isPartnerAccount ? 'Update unsuccessful' : 'Error updating billing account')
        .withMessage(isPartnerAccount ? 'Try again or contact Partner service desk.' : '');

      if (errorList) {
        const message = errorList.map((DetailItem, index) => (
          <>
            <DetailItem key={index} />
          </>
        ));
        notificationOptions.withMessage(message);
      }
      const notification = notificationOptions.build();
      sendErrorNotification(notification);
      setNotificationSent(true);
    },
    [setNotificationSent, sendErrorNotification]
  );

  const { isProcessing, updateTransactionAccount } = useUpdateTransactionAccount({
    onClose,
    onComplete: onUpdateTransactionComplete,
    sendNotification: false,
    onError: onUpdateTransactionError,
  });
  // To track if account creation is pending in UpdateBillingAdminsFields and save button should be disabled.
  const [isSaveButtonDisabled, setDisableSaveButton] = useState<boolean>(false);

  // Need this since we can't call hook from the nested function submitBillingDetails.
  // P.S. This is being done in order to bypass the permissions as it is being used in PPC MVP
  const addBillingAdminPermission = useRef(isAccess(Permissions.CONTACT_WRITE) || shouldForceAddBillingAdmin);

  if (!transactionAccountId) return <p>No transaction account ID found</p>;

  const onSubmit = (data: FormData, formApi: FormApi<FormData>) => {
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(data, key);
      set(transformedData, key, value);
    });

    const billingAdminsOnForm = transformedData.recipients;

    const billingAdminsOnFile = billingAdmins
      // @ts-ignore
      .map((item: { email: string }) => item.email)
      .filter((emailOrAaid) => !isNil(emailOrAaid));

    const newAddedBillingAdminsList = billingAdminsOnForm
      ?.filter((admin: string) => !billingAdminsOnFile?.includes(admin))
      .filter((admin) => Boolean(admin));

    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      invoiceGroupId,
      modifiedBillingAdmins: {
        emailsOrAaidsToAdd:
          addBillingAdminPermission.current && newAddedBillingAdminsList ? newAddedBillingAdminsList : [], // to check if their is a permission to add billing admin.
      },
    };

    updateTransactionAccount({
      transactionAccountId,
      updateTransactionAccountPayload,
    });
  };

  return (
    <div className={`add-billing-admin-form-container ${sideBarClassName}`}>
      <Form onSubmit={onSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={header || 'Add a billing admin'} description={subheader || ''} />
            {isReactivateFlow && (
              <FormSection>
                <InfoPanel
                  type="information"
                  message="You'll need to at least one billing admin to reactive this billing account."
                  title="Add billing admins"
                />
              </FormSection>
            )}
            {isSubtextHidden || (
              <FormSection>
                <p className="form-title">
                  To ensure access to billing info at all times, we recommend that billing accounts have more than one
                  billing admin.
                  <br /> You&apos;ll have the option to create Atlassian accounts when you add unregistered email
                  addresses
                </p>
              </FormSection>
            )}
            <FormSection>
              <AddBillingAdminsFields
                billingAdmins={[]}
                disableSaveButton={(status: boolean) => {
                  setDisableSaveButton(status);
                }}
                isRequired={true}
                placeholder={'Enter email address'}
                helperMessage={'An active billing account must have atleast 1 billing admin'}
                title={'Billing admins'}
                validDomains={validDomains}
                isPartnerAccount={isPartnerAccount}
                allowToAddBillingAdmin={isAccess(Permissions.CONTACT_WRITE)} // for checking is user has enough permissions to add billign admin
              />
            </FormSection>
            <div className="divider" />
            <div className="form-footer">
              <FormFooter align="end">
                <ButtonGroup>
                  <Button appearance="subtle" onClick={onClose}>
                    Cancel
                  </Button>
                  <LoadingButton
                    appearance="primary"
                    type="submit"
                    isLoading={isProcessing}
                    isDisabled={isSaveButtonDisabled}
                    testId={isProcessing ? 'add-admin-processing-button' : 'add-admin-save-button'}
                  >
                    {isReactivateFlow ? 'Add billing admin & reactivate' : 'Add admin'}
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};

export default AddBillingAdminsForm;
